import { getContactDuplicates } from '@media-lists/services/mediaListsService';
import {
  ContactBeingAdded,
  ContactDuplicateId,
  DiscoveryContactDetails,
} from '@src/types/duplicateContacts';
import { getSkippedContactIds } from '@src/utils/duplicateContacts';

import { useDuplicateContactsModal } from './DuplicateContactsModalContext';
import { resolveDuplicatedContacts } from './resolveDuplicatedContacts';

interface ResolveDuplicatesBeforeAddingParams {
  contactsBeingAdded: ContactBeingAdded[];
  discoveryContacts?: Record<string, DiscoveryContactDetails>;
  mediaListsId: number[];
}

type DuplicateContactsDecision =
  | { isCancelled: true }
  | { isCancelled: false; contactIdsToSkip: Set<ContactDuplicateId> };

export class DuplicateContactsSelectionCancelledError extends Error {}

export const useResolveDuplicatesBeforeAdding = () => {
  const { requestDuplicateContactsSelection } = useDuplicateContactsModal();

  return async ({
    contactsBeingAdded,
    discoveryContacts,
    mediaListsId,
  }: ResolveDuplicatesBeforeAddingParams): Promise<DuplicateContactsDecision> => {
    const { duplicatedContacts } = await getContactDuplicates({
      contacts: contactsBeingAdded,
      discoveryContacts,
      mediaListsId,
    });

    if (duplicatedContacts.length === 0) {
      return { isCancelled: false, contactIdsToSkip: new Set() };
    }

    const contactIdsBeingAdded = new Set(contactsBeingAdded.map(({ id }) => id));
    const selectedContactIds = await requestDuplicateContactsSelection({
      duplicatedContacts,
      addedContactIds: [...contactIdsBeingAdded],
    });

    if (!selectedContactIds) {
      return { isCancelled: true };
    }

    await resolveDuplicatedContacts(
      duplicatedContacts,
      selectedContactIds,
      mediaListsId,
      contactIdsBeingAdded,
    );

    return {
      isCancelled: false,
      contactIdsToSkip: getSkippedContactIds(
        duplicatedContacts,
        selectedContactIds,
        contactIdsBeingAdded,
      ),
    };
  };
};
